import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
// Service
import { BroadcastService, ObjectUtils } from '@cv-invision/core';
import { ViewsService } from '../../../../../services/views.service';
import { SchemasService } from '../../../../../services/schemas.service';
import { LayoutRenderService } from '../../../services/layout-render.service';
// Model
import { BroadcastEventModel } from '@cv-invision/core';
import {
	LayoutComponentBroadcast,
	LayoutRenderLayoutItem,
} from '../../../models/cv-layout-render.model';
import { BroadcastTabsEventMetadata, GeneralEventType, TabsEventType } from '../../models/component.events';
import { FORM_EVENT } from '../../../../../models/enums.model';
import { TabSectionMode, TabsSectionOptions } from '../../models/component-options/tabs-section.options';

@Component({
	selector: 'cv-schema-render-tabs-section',
	templateUrl: './tabs-section.component.html'
})
export class TabsSectionComponent implements OnInit, OnDestroy {
	@Input() data: any;
	@Input() layoutNode = new LayoutRenderLayoutItem();

	public tabSectionMode = TabSectionMode;
	public options: TabsSectionOptions;
	tabs: any;
	tabsData: any = [];
	selectedItem: any = null;
	defaultSelectedItem: any = null;
	tabsInitialized = false;

	get flattenedPhotos(): any {
		return this.viewsService.flattenedPhotos;
	}

	get selectedPhotos(): any {
		return this.viewsService.photosSelected;
	}

	get conditionPhotos(): any {
		return {
			flattenedPhotos: this.flattenedPhotos,
			selectedPhotos: this.selectedPhotos,
		};
	}

	get isEditable(): boolean {
		return this.layoutRenderService.extraData.isEditing;
	}

	protected atComponentDestroy = new Subject();

	constructor(
		private broadcastService: BroadcastService,
		private viewsService: ViewsService,
		private schemasService: SchemasService,
		private layoutRenderService: LayoutRenderService
	) {
		this.broadcastService
			.onEvent(FORM_EVENT.PHOTO_SELECT)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((broadcast: BroadcastEventModel) => {
				if ('jsonPath' in broadcast.event && broadcast.event.jsonPath === this.data._Path) {
					this.getTabData();
				}
			});

		this.layoutRenderService.componentBroadcast
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((data: LayoutComponentBroadcast) => {
				if (data.jsonPath === this.data._Path) {
					const metadata = new BroadcastTabsEventMetadata();
					if (data.metaData) {
						Object.assign(metadata, data.metaData);
					}

					switch (data.eventType) {
						case GeneralEventType.DATA_UPDATED:
						case TabsEventType.UPDATE_TABS:
							this.getTabData();
							break;

						case TabsEventType.GET_ACTIVE_TAB:
							if (metadata.reloadData) {
								this.getTabData();
							}
							this.select(this.getActiveTab());
							break;
					}
				}
			});
	}

	ngOnInit(): void {
		this.options = { ...this.options, ...this.layoutNode.options };

		this.tabs = ObjectUtils.recursiveDeepCopy(this.layoutNode.items);
		this.getTabData();
		// Timeout for AJSF
		setTimeout(() => {
			this.select(this.getActiveTab());
			this.tabsInitialized = true;
		});
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}

	/**
	 * Get each Tab data and store it inside the layout item
	 */
	getTabData(): void {
		this.tabs.forEach((item: any, itemIndex: number) => {
			const id = `tab_${this.data._Data.section}-${this.data._Data.index.join('-')}_${itemIndex}`;
			item.options = { ...item.options, ...{ photosInData: true } };

			let path = this.data._Path;
			let formData = this.data;
			let photos = this.flattenedPhotos[path] || [];
			let visible = false;
			const filters: any = [];

			if ('key' in item.options) {
				path = `${path}.${item.options.key}`;
				photos = [];
				formData = null;
				const searchData = this.schemasService.getQuery(this.data, item.options.key) || [];

				if (searchData[0].length) {
					formData = searchData[0];
					formData.forEach((fdItem: any) => {
						const fdPhotos = this.flattenedPhotos[fdItem._Path] || [];
						if (fdPhotos.length) {
							photos = photos.concat(fdPhotos[0]);
						}
					})
				}
			}

			if ('filter' in item.options) {
				if (typeof item.options.filter === 'string') {
					filters.push(item.options.filter.toUpperCase());
				} else if (item.options.filter instanceof Array) {
					item.options.filter.forEach((filter: any) => {
						if (filter) {
							filters.push(filter.toUpperCase());
						}
					});
				}
			}

			if (filters.length) {
				photos = photos.filter((photo: any) => filters.indexOf(photo.metadata.imageType) > -1);
			}

			if (item.type === 'photo-selected' || 'condition' in item.options) {
				photos = [];
				// views.service.ts@19
				Object.keys(this.flattenedPhotos).forEach((value: any) => {
					if (value.indexOf(path) > -1) {
						const imagesSelected = (this.flattenedPhotos[value] || []).filter((photo: any) => this.selectedPhotos.indexOf(photo.id) > -1);
						photos = photos.concat(imagesSelected);
					}
				});
			}

			visible = formData && photos.length > 0;

			// Tab Sections and Report Images always visible, so the user can add a new one.
			if (item.type === 'tabs-section-button' || item.options.showIfEmpty) {
				visible = true;
			}

			// Back compatibility
			const tabSectionMode = (!item.options.tabSectionMode) ? TabSectionMode.TAB : item.options.tabSectionMode;

			const itemData = {
				id,
				path,
				data: {
					data: formData,
					photos
				},
				visible,
				tabSectionMode
			};
			this.tabsData[itemIndex] = { ...itemData };
		});
	}

	/**
	 * Get Active tab deps. on the isVisible attr.
	 *
	 */
	getActiveTab(): number | null {
		const activeTabs = this.tabsData.filter((val: any, index: any) => {
			val.originalIndex = index;
			return val.visible;
		});

		if (activeTabs.length > 0) {
			const firstTab = activeTabs[0];
			this.defaultSelectedItem = firstTab.originalIndex;
			return firstTab.originalIndex;
		}

		return null;
	}

	/**
	 * Set Tab index as selected
	 *
	 * @param index
	 */
	select(index: any, event?: any): any {

		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}
		this.selectedItem = index;

		if (this.tabs[index].type === '3d-tour') {
			this.broadcastService.broadcast({
				name: FORM_EVENT.TELEMETRY,
				event: {
					telemetryEventName: '3dTour-START',
					data: {
						path: this.data._Path
					}
				}
			});
		}

		return false;
	}

	/*
	 * Check if tab is visible or not
	 * @param item
	 */
	isVisible(itemIndex: number): boolean {
		const tabData = this.tabs[itemIndex];
		const itemData = this.tabsData[itemIndex];
		let response = itemData.visible;

		if (itemData.visible && tabData.options.showIfEditMode) {
			response = this.isEditable;
		}

		return response;
	}

}
